import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress } from "@mui/material";
import LocalFloristIcon from "@mui/icons-material/LocalFlorist";
import PeopleIcon from "@mui/icons-material/People";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import FornecedorService from "../../../service/fornecedor_service";
import ProdutoService from "../../../service/produto_service";
import ColetaService from "../../../service/coleta_service";
import KpiCard from "./KpiCard";
import ReportFilters from "./ReportFilters";
import ExportButtons from "./ExportButtons";
import { exportToPDF, exportToExcel } from "../../../utils/exportUtils";
import { toLocalISODate } from "../../../utils/dateUtils";

const PIE_COLORS = ["#1a9e8f", "#f5a623"];

function getDefaultDates() {
	const hoje = new Date();
	const inicio = new Date(hoje);
	inicio.setDate(inicio.getDate() - 30);
	return {
		from: toLocalISODate(inicio),
		to: toLocalISODate(hoje),
	};
}

function ReportFornecimento() {
	const defaults = getDefaultDates();
	const [dateFrom, setDateFrom] = useState(defaults.from);
	const [dateTo, setDateTo] = useState(defaults.to);
	const [loading, setLoading] = useState(false);
	const [stats, setStats] = useState({ produtos: 0, fornecedores: 0, coletas: 0 });
	const [coletasPorFornecedor, setColetasPorFornecedor] = useState([]);
	const [produtosPorVolume, setProdutosPorVolume] = useState([]);
	const [statusColetas, setStatusColetas] = useState([]);

	useEffect(() => {
		loadData();
	}, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

	const loadData = async () => {
		try {
			setLoading(true);
			const unidadeId = localStorage.getItem("selected_unidade_id");
			if (!unidadeId) return;

			const fornecedorService = new FornecedorService();
			const produtoService = new ProdutoService();
			const coletaService = new ColetaService();

			const [fornecedoresRes, produtosRes] = await Promise.all([
				fornecedorService.getByUnidade(unidadeId).catch(() => ({ data: [] })),
				produtoService.getByUnidade(unidadeId, "entrada").catch(() => ({ data: [] })),
			]);

			const fornecedoresData = fornecedoresRes.data || [];
			const produtosData = Array.isArray(produtosRes) ? produtosRes : (produtosRes.data || []);

			let totalColetas = 0;
			let coletasCompletas = 0;
			let coletasPendentes = 0;
			const fornecedorColetas = {};
			const produtoVolumes = {};

			const startDate = new Date(dateFrom + "T12:00:00");
			const endDate = new Date(dateTo + "T12:00:00");
			for (let date = new Date(endDate); date >= startDate; date.setDate(date.getDate() - 1)) {
				const dateStr = toLocalISODate(date);

				try {
					const coletasRes = await coletaService.getColetasPorData(unidadeId, dateStr);
					const coletas = coletasRes.data?.data || [];
					totalColetas += coletas.length;

					for (const c of coletas) {
						if (c.status === "CONFIRMADO") {
							coletasCompletas++;
						} else {
							coletasPendentes++;
						}

						const fornecedorId = c.fornecedorId || "sem-fornecedor";
						const fornecedorNome = c.fornecedor?.nome || "Sem fornecedor";
						if (!fornecedorColetas[fornecedorId]) {
							fornecedorColetas[fornecedorId] = { nome: fornecedorNome, coletas: 0, produtos: new Set() };
						}
						fornecedorColetas[fornecedorId].coletas++;
						if (c.produtoUnidadeDistribuidoraId) {
							fornecedorColetas[fornecedorId].produtos.add(c.produtoUnidadeDistribuidoraId);
						}

						const produtoId = c.produtoUnidadeDistribuidoraId || "sem-produto";
						const produtoNome = c.produtoUnidadeDistribuidora?.cultivo?.cultura || "Produto";
						if (!produtoVolumes[produtoId]) {
							produtoVolumes[produtoId] = { nome: produtoNome, total: 0, fornecedores: new Set() };
						}
						produtoVolumes[produtoId].total += c.quantidade || 0;
						if (c.fornecedorId) {
							produtoVolumes[produtoId].fornecedores.add(c.fornecedorId);
						}
					}
				} catch (_) {
					// Ignora erros de datas sem coletas
				}
			}

			setStats({ produtos: produtosData.length, fornecedores: fornecedoresData.length, coletas: totalColetas });

			setStatusColetas([
				{ name: "Confirmado", value: coletasCompletas },
				{ name: "Pendente", value: coletasPendentes },
			]);

			setColetasPorFornecedor(
				Object.values(fornecedorColetas)
					.map((f) => ({ nome: f.nome, coletas: f.coletas, produtos: f.produtos.size }))
					.sort((a, b) => b.coletas - a.coletas)
			);

			setProdutosPorVolume(
				Object.values(produtoVolumes)
					.map((p) => ({ nome: p.nome, total: Math.round(p.total), fornecedores: p.fornecedores.size }))
					.sort((a, b) => b.total - a.total)
			);
		} catch (error) {
			console.error("Erro ao carregar relatórios de fornecimento:", error);
		} finally {
			setLoading(false);
		}
	};

	const getExportConfig = () => ({
		title: "Relatório de Fornecimento",
		filename: "fornecimento",
		kpis: [
			{ label: "Produtos", value: String(stats.produtos) },
			{ label: "Fornecedores", value: String(stats.fornecedores) },
			{ label: "Coletas", value: String(stats.coletas) },
		],
		headers: ["Fornecedor", "Coletas", "Produtos"],
		rows: coletasPorFornecedor.map((f) => [f.nome, f.coletas, f.produtos]),
	});

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
			<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
				<ReportFilters
					searchValue=""
					onSearchChange={() => {}}
					searchPlaceholder=""
					dateFrom={dateFrom}
					dateTo={dateTo}
					onDateFromChange={setDateFrom}
					onDateToChange={setDateTo}
				/>
			</Box>

			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6, gap: 1, color: "#8e9bae" }}>
					<CircularProgress size={20} /> <Typography variant="body2">Carregando dados de fornecimento...</Typography>
				</Box>
			) : (<>
			<Grid container spacing={2}>
				<Grid size={{ xs: 4 }}>
					<KpiCard icon={<LocalFloristIcon />} title="Produtos" value={stats.produtos} />
				</Grid>
				<Grid size={{ xs: 4 }}>
					<KpiCard icon={<PeopleIcon />} title="Fornecedores" value={stats.fornecedores} />
				</Grid>
				<Grid size={{ xs: 4 }}>
					<KpiCard icon={<TrendingUpIcon />} title="Coletas" value={stats.coletas} />
				</Grid>
			</Grid>

			<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
				<ExportButtons onExportPDF={() => exportToPDF(getExportConfig())} onExportExcel={() => exportToExcel(getExportConfig())} />
			</Box>

			{/* Gráficos */}
			<Grid container spacing={3} sx={{ alignItems: "stretch" }}>
				<Grid size={{ xs: 12, lg: 8 }}>
					<Card sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8", height: "100%" }}>
						<CardContent sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
							<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0f4557", mb: 2, fontSize: "0.9rem" }}>
								Top Produtos por Quantidade
							</Typography>
							<Box sx={{ flex: 1, minHeight: 260 }}>
								{produtosPorVolume.length > 0 ? (
									<ResponsiveContainer width="100%" height="100%">
										<BarChart data={produtosPorVolume.slice(0, 5)} layout="vertical" margin={{ left: 0, right: 8 }}>
											<CartesianGrid strokeDasharray="3 3" stroke="#dfe3e8" />
											<XAxis type="number" tick={{ fontSize: 11, fill: "#5a7a88" }} />
											<YAxis dataKey="nome" type="category" width={100} tick={{ fontSize: 11, fill: "#5a7a88" }} />
											<Tooltip contentStyle={{ borderRadius: "8px", fontSize: 12 }} />
											<Bar dataKey="total" fill="#1a9e8f" radius={[0, 6, 6, 0]} />
										</BarChart>
									</ResponsiveContainer>
								) : (
									<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#8e9bae" }}>
										<Typography variant="body2">Nenhum dado no período</Typography>
									</Box>
								)}
							</Box>
						</CardContent>
					</Card>
				</Grid>

				<Grid size={{ xs: 12, lg: 4 }}>
					<Card sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8", height: "100%" }}>
						<CardContent sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
							<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0f4557", mb: 2, fontSize: "0.9rem" }}>
								Status das Coletas
							</Typography>
							<Box sx={{ flex: 1, minHeight: 200 }}>
								{statusColetas.some(s => s.value > 0) ? (
									<ResponsiveContainer width="100%" height="100%">
										<PieChart>
											<Pie data={statusColetas} cx="50%" cy="50%" innerRadius={50} outerRadius={75} dataKey="value" paddingAngle={4}>
												{statusColetas.map((_, i) => (
													<Cell key={i} fill={PIE_COLORS[i]} />
												))}
											</Pie>
											<Tooltip contentStyle={{ borderRadius: "8px", fontSize: 12 }} />
										</PieChart>
									</ResponsiveContainer>
								) : (
									<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#8e9bae" }}>
										<Typography variant="body2">Nenhum dado no período</Typography>
									</Box>
								)}
							</Box>
							<Box sx={{ display: "flex", justifyContent: "center", gap: 3, mt: 1 }}>
								{statusColetas.map((s, i) => (
									<Box key={s.name} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<Box sx={{ width: 12, height: 12, borderRadius: "2px", bgcolor: PIE_COLORS[i] }} />
										<Typography variant="caption" sx={{ color: "#5a7a88" }}>{s.name} ({s.value})</Typography>
									</Box>
								))}
							</Box>
						</CardContent>
					</Card>
				</Grid>
			</Grid>

			<Grid container spacing={3}>
				{/* Produtos por Volume */}
				<Grid size={{ xs: 12, lg: 6 }}>
					<Card sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8", height: 430 }}>
						<CardContent>
							<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0f4557", mb: 2, fontSize: "0.9rem" }}>
								Produtos por Volume
							</Typography>
							<TableContainer sx={{ maxHeight: 350 }}>
								<Table size="small" stickyHeader>
									<TableHead>
										<TableRow>
											<TableCell sx={{ fontWeight: 600, color: "#8e9bae" }}>Produto</TableCell>
											<TableCell sx={{ fontWeight: 600, color: "#8e9bae" }}>Total</TableCell>
											<TableCell sx={{ fontWeight: 600, color: "#8e9bae" }}>Fornecedores</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{produtosPorVolume.map((p, i) => (
											<TableRow key={i}>
												<TableCell sx={{ fontWeight: 500, color: "#0f4557" }}>{p.nome}</TableCell>
												<TableCell sx={{ fontWeight: 600, color: "#1a9e8f" }}>{p.total}</TableCell>
												<TableCell sx={{ color: "#5a7a88" }}>{p.fornecedores}</TableCell>
											</TableRow>
										))}
										{produtosPorVolume.length === 0 && (
											<TableRow>
												<TableCell colSpan={3} sx={{ textAlign: "center", color: "#8e9bae", py: 4 }}>Nenhum dado disponível</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table>
							</TableContainer>
						</CardContent>
					</Card>
				</Grid>

				{/* Participação dos Fornecedores */}
				<Grid size={{ xs: 12, lg: 6 }}>
					<Card sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8", height: 430 }}>
						<CardContent>
							<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0f4557", mb: 2, fontSize: "0.9rem" }}>
								Participação dos Fornecedores
							</Typography>
							<TableContainer sx={{ maxHeight: 350 }}>
								<Table size="small" stickyHeader>
									<TableHead>
										<TableRow>
											<TableCell sx={{ fontWeight: 600, color: "#8e9bae" }}>Fornecedor</TableCell>
											<TableCell sx={{ fontWeight: 600, color: "#8e9bae" }}>Coletas</TableCell>
											<TableCell sx={{ fontWeight: 600, color: "#8e9bae" }}>Produtos</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{coletasPorFornecedor.map((f, i) => (
											<TableRow key={i}>
												<TableCell>
													<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
														<Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: "#1a9e8f", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "0.75rem", fontWeight: 700 }}>
															{f.nome.charAt(0)}
														</Box>
														<Typography variant="body2" sx={{ fontWeight: 500, color: "#0f4557" }}>{f.nome}</Typography>
													</Box>
												</TableCell>
												<TableCell>
													<Box sx={{ display: "inline-block", px: 1.5, py: 0.25, borderRadius: "12px", bgcolor: "#1a9e8f15", color: "#1a9e8f", fontSize: "0.75rem", fontWeight: 500 }}>
														{f.coletas}
													</Box>
												</TableCell>
												<TableCell sx={{ color: "#5a7a88" }}>{f.produtos}</TableCell>
											</TableRow>
										))}
										{coletasPorFornecedor.length === 0 && (
											<TableRow>
												<TableCell colSpan={3} sx={{ textAlign: "center", color: "#8e9bae", py: 4 }}>Nenhum dado disponível</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table>
							</TableContainer>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			</>)}
		</Box>
	);
}

export default ReportFornecimento;
